import { Eq } from 'fp-ts/lib/Eq';
import { empty, singleton, union } from 'fp-ts/lib/Set';
import * as t from 'io-ts';
import { reporter } from 'io-ts-reporters';

import { CastError, EnumMemberNotFoundError } from '../errors';

/**
 * Cast an unknown value using an `io-ts` schema. If validation fails, throw an error.
 * If it passes, cast the value to the schema's interface.
 * @param {unknown} data - Object to validate
 * @param {T} schema - Schema to validate against
 * @param {boolean?} includeDataInError - Whether to include data in error or not.
 * @returns {t.TypeOf<T>} the validated object
 */
export function cast<T extends t.Any>(
  data: unknown,
  schema: T,
  includeDataInError = false,
): t.TypeOf<T> {
  const decoded = schema.decode(data);

  if (decoded.isLeft()) {
    const errors = reporter(decoded);
    throw new CastError(errors, includeDataInError ? data : undefined);
  }

  return data as t.TypeOf<typeof schema>;
}

/**
 * Convert a typescript enum to an io-ts enum
 * @see https://github.com/gcanti/io-ts/issues/216
 * @example backend/src/types/delivery.ts
 */
export class Enum<T> extends t.Type<T> {
  // tslint:disable-next-line:variable-name
  // eslint-disable-next-line @typescript-eslint/prefer-as-const
  public readonly _tag: 'EnumType' = 'EnumType';
  public enumObject!: object;
  public constructor(e: object, name?: string) {
    super(
      name || 'enum',
      (u): u is T => Object.values(this.enumObject).some((v) => v === u),
      (u, c) => (this.is(u) ? t.success(u) : t.failure(u, c)),
      t.identity,
    );
    this.enumObject = e;
  }
}

/**
 * Given an arbitrary enumeration, generate the reverse lookup map.
 *
 * @param {object} enumeration
 * The enumeration.
 *
 * @returns {Map}
 * A Mapping where the key is the value and the value is the key.
 */
export function enumReverseMap<E extends object>(
  enumeration: E,
): Map<{ [K in keyof E]: E[K] }[keyof E], keyof E> {
  return new Map(Object.entries(enumeration).map(([key, value]) => [value, key as keyof E]));
}

/**
 * Given a constructor, cast it as a singleton.
 *
 * @param {T} constructor
 * A constructor.
 *
 * @returns {T}
 * The constructor is returned, wrapped in Proxy such that it returns the first instance
 * of itself.
 */
export function asSingleton<
  A extends ConstructorParameters<T>,
  T extends new (...args: any[]) => {}
>(constructor: T): T {
  let instance: T;
  return new Proxy<T>(constructor, {
    construct: (target: T, args: A): T => ((instance = instance || new target(...args)), instance),
  });
}

/**
 * Given an instance of `Eq<V>`, returns a function to fold an array of `V`s
 * into a set of `V`s
 */
export const fold = <V>(E: Eq<V>): ((vs: V[]) => Set<V>) => (vs: V[]): Set<V> =>
  vs.reduce((acc: Set<V>, v: V) => union<V>(E)(acc, singleton(v)), empty);

/**
 * assert a given value appears in the given enumeration
 * @throws {Error} an error if the value does not appear in the given enumeration
 */
export function assertIsEnumValue<T extends string | number>(
  value: string | number,
  sourceEnum: Record<string, T>,
): asserts value is T {
  const enumValues = Object.values(sourceEnum);

  if (!enumValues.includes(value as T)) {
    throw new EnumMemberNotFoundError(value, enumValues);
  }
}

/**
 * Accepts a string | number and casts it to an enum type, throwing an
 * error if the given value is not assignable to the given enum
 */
export const coerceEnumValue = <T extends string | number>(
  value: string | number,
  sourceEnum: Record<string, T>,
): T => {
  assertIsEnumValue<T>(value, sourceEnum);

  return value;
};
